import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { navigateToHome, isOnHomepage, isOnLoginPage } from '../config/frontend';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Define logout function first using useCallback
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setShouldNavigate(false);
      setJustLoggedIn(false);
      
      // Clear welcome message flags for all users
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('welcome-shown-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Set flag for logout message on homepage
      localStorage.setItem('was_logged_out', 'true');
      
      // Navigate to home page
      navigateToHome();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
      setShouldNavigate(false);
      setJustLoggedIn(false);
      
      // Clear welcome message flags for all users
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('welcome-shown-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Set flag for logout message on homepage
      localStorage.setItem('was_logged_out', 'true');
      
      navigateToHome();
    }
  }, []);


  // Initialize authentication state on mount - fetch from server
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have a token first
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setIsInitialized(true);
          return;
        }
        
        // Fetch user data from server to ensure we have latest roles/permissions
        const response = await authService.apiRequest('/auth/me');
        if (response && response.success) {
          const currentUser = response.user;
          setUser(currentUser);
          setIsAuthenticated(true);
          
          // Only set shouldNavigate to true if we're on the homepage
          if (isOnHomepage()) {
            setShouldNavigate(true);
          }
        } else {
          // Clear any stale localStorage data
          authService.logout();
        }
        
        // Mark as initialized
        setIsInitialized(true);
        
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear any stale localStorage data on error
        authService.logout();
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []); // Empty dependency array - run only once on mount

  const login = async (emailOrUsername, password, rememberMe = false) => {
    try {
      const result = await authService.login(emailOrUsername, password, rememberMe);
      
      if (result.success) {
        // Success - update state and mark as just logged in
        setUser(result.user);
        setIsAuthenticated(true);
        setJustLoggedIn(true);
        
        // Only navigate if we're on the homepage
        if (isOnHomepage()) {
          setShouldNavigate(true);
        }
        
        return { success: true, message: result.message };
      } else {
        // Error - return error message, stay on homepage
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = 'Network error. Please check your connection.';
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };


  // Clear just logged in state after showing welcome message
  const clearJustLoggedIn = () => {
    setJustLoggedIn(false);
  };

  // Update user data
  const updateUser = (updatedUserData) => {
    setUser(prevUser => {
      const updatedUser = {
        ...prevUser,
        ...updatedUserData
      };
      return updatedUser;
    });
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await authService.apiRequest('/auth/me');
      if (response.success) {
        const updatedUser = response.user;
        setUser(updatedUser);
        return updatedUser;
      } else {
        console.error('❌ Failed to refresh user data:', response.message);
      }
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
    }
    return null;
  };

  const value = {
    user,
    isAuthenticated,
    shouldNavigate,
    justLoggedIn,
    isInitialized,
    login,
    logout,
    clearJustLoggedIn,
    updateUser,
    refreshUser,
    authService,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
